import { mergeRegister } from "@lexical/utils";
import {
  $createTextNode,
  $getNearestNodeFromDOMNode,
  $getNodeByKey,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_LOW,
  defineExtension,
  safeCast,
  SELECTION_CHANGE_COMMAND,
  type LexicalExtension,
  type NodeKey,
  type PointType,
} from "lexical";
import { CONTENT_CLASSES } from "../../core/content-classes";
import {
  CORE_TOKEN_REGISTRY,
  tokenLabel,
  type InlineToken,
  type TokenRegistry,
} from "../../core/tokens";
import { $isTokenNode, registries, TokenNode } from "./node";

export interface TokensConfig {
  registry: TokenRegistry;
  /** A ref, because the host's handler changes between renders and the editor outlives them. */
  activate: { current: ((token: InlineToken) => void) | null };
}

interface Position {
  key: NodeKey;
  offset: number;
}

/**
 * Moves a point that sits strictly inside a token to one of its edges: onward in the direction the
 * caret was travelling, or to the nearer edge when that is unknown (a click). A token is one unit
 * to the caret. Without this, Lexical lets the caret walk through the label, and a key typed there
 * replaces the whole token with that one character.
 */
function $snapOutOfToken(
  point: PointType,
  previous: Position | null,
  toward?: "start" | "end",
): void {
  const node = point.getNode();
  if (!$isTokenNode(node)) return;
  const size = node.getTextContentSize();
  if (point.offset <= 0 || point.offset >= size) return;
  let edge = toward;
  if (edge === undefined && previous !== null) {
    if (previous.key === node.getKey()) edge = point.offset > previous.offset ? "end" : "start";
    else {
      const from = $getNodeByKey(previous.key);
      if (from !== null && from.isAttached()) edge = from.isBefore(node) ? "end" : "start";
    }
  }
  edge ??= point.offset * 2 >= size ? "end" : "start";
  point.set(node.getKey(), edge === "end" ? size : 0, "text");
}

/**
 * A token is its whole label or it is not a token. Anything that leaves one with other text (a
 * split through the middle of it, for one) would otherwise save a node whose words and payload
 * disagree, which validation rightly refuses to load. So such a node becomes the plain text it now
 * is: no characters lost, no reference duplicated across the pieces, nothing unreadable written.
 */
function $keepTokenWhole(node: TokenNode): void {
  const token = node.getToken();
  if (token !== null && node.getTextContent() === tokenLabel(token)) {
    if (!node.isToken()) node.setMode("token");
    return;
  }
  const text = $createTextNode(node.getTextContent());
  text.setFormat(node.getFormat());
  text.setStyle(node.getStyle());
  node.replace(text);
}

/**
 * Registers the token node in every preset (a preset never decides what can be loaded) and turns a
 * click on a token into a call to the host. Activation says what was clicked and nothing more: the
 * host decides whether the user may open it.
 */
export const TokensExtension: LexicalExtension<TokensConfig, "z-editor/tokens", unknown, unknown> =
  defineExtension({
    name: "z-editor/tokens",
    nodes: [TokenNode],
    config: safeCast<TokensConfig>({ registry: CORE_TOKEN_REGISTRY, activate: { current: null } }),
    register(editor, config) {
      registries.set(editor, config.registry);
      const onClick = (event: MouseEvent): void => {
        const activate = config.activate.current;
        const target =
          event.target instanceof Element
            ? event.target.closest(`.${CONTENT_CLASSES.token}`)
            : null;
        if (activate === null || target === null) return;
        const token = editor.read(() => {
          const node = $getNearestNodeFromDOMNode(target);
          return $isTokenNode(node) ? node.getToken() : null;
        });
        if (token !== null) activate(token);
      };
      // Where the caret was before this change: the selection of the last committed state, which
      // is right whether the caret got here by an arrow key, an edit, or a click.
      const previousFocus = (): Position | null =>
        editor.getEditorState().read(() => {
          const selection = $getSelection();
          return $isRangeSelection(selection)
            ? { key: selection.focus.key, offset: selection.focus.offset }
            : null;
        });
      return mergeRegister(
        editor.registerNodeTransform(TokenNode, $keepTokenWhole),
        editor.registerRootListener((root, previous) => {
          previous?.removeEventListener("click", onClick);
          root?.addEventListener("click", onClick);
        }),
        editor.registerCommand(
          SELECTION_CHANGE_COMMAND,
          () => {
            const selection = $getSelection();
            if (!$isRangeSelection(selection)) return false;
            const previous = previousFocus();
            if (selection.isCollapsed()) {
              const { focus } = selection;
              const { key, offset } = focus;
              $snapOutOfToken(focus, previous);
              // Only when the caret was actually moved. Writing to a point marks the selection
              // dirty, Lexical then applies it to the DOM again, and for a caret at the root (after
              // a table or a divider that ends the document) Firefox answers every such write with
              // another `selectionchange`: tens of thousands a second, for as long as it rests there.
              if (focus.key !== key || focus.offset !== offset) {
                selection.anchor.set(focus.key, focus.offset, focus.type);
              }
            } else {
              // A range grows over the whole token, and starts outside it.
              $snapOutOfToken(selection.focus, previous);
              $snapOutOfToken(selection.anchor, null, selection.isBackward() ? "end" : "start");
            }
            return false;
          },
          COMMAND_PRIORITY_LOW,
        ),
      );
    },
  });
